import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EmailSchema,
  PasswordSchema,
  type MeResponse,
  type PasswordSetupBody,
} from '../../../src/shared/auth';
import {
  authKeys,
  fetchMe,
  fetchPasswordStatus,
  passwordSetup,
  passwordStatusKey,
} from '../../api/auth';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';

/**
 * `/admin/setup` — one-time super-user password claim (first run). Open only
 * while `GET /api/auth/password/status` reports `setupRequired`; the email
 * must match the operator-seeded platform-admin user. The password is chosen
 * here, in the browser — it never transits anywhere but this form's POST.
 */

/** The public site key is build-time client config; read lazily so tests can stub env. */
function turnstileSiteKey(): string {
  return import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
}

export function AdminSetupPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: passwordStatusKey,
    queryFn: ({ signal }) => fetchPasswordStatus(signal),
  });

  const setupMutation = useMutation({
    mutationFn: async (body: PasswordSetupBody): Promise<MeResponse> => {
      await passwordSetup(body);
      return fetchMe();
    },
    onSuccess: (me) => {
      queryClient.setQueryData(authKeys.me, me);
      void queryClient.invalidateQueries({ queryKey: passwordStatusKey });
      void navigate('/admin', { replace: true });
    },
  });

  if (status.isPending) {
    return <LoadingScreen />;
  }
  if (status.isSuccess && !status.data.setupRequired) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedEmail = EmailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setFieldError(parsedEmail.error.issues[0]?.message ?? 'Enter a valid email address.');
      return;
    }
    const parsedPassword = PasswordSchema.safeParse(password);
    if (!parsedPassword.success) {
      setFieldError(parsedPassword.error.issues[0]?.message ?? 'Choose a longer password.');
      return;
    }
    if (password !== confirm) {
      setFieldError('Passwords do not match.');
      return;
    }
    setFieldError(null);
    if (turnstileToken === null) {
      return;
    }
    setupMutation.mutate({
      email: parsedEmail.data,
      password: parsedPassword.data,
      turnstileToken,
    });
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-semibold tracking-tight">Super-user setup</h1>
        <p className="mt-2 text-slate-600">
          One-time setup: choose the password for the seeded platform-admin account. This screen
          closes permanently once a password exists.
        </p>

        <section className="mt-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <form className="mt-1 space-y-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="setup-email" className="block text-sm font-medium text-slate-700">
                Platform-admin email
              </label>
              <input
                id="setup-email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
              <p className="mt-1 text-sm text-slate-500">
                Must match the admin user seeded in the database.
              </p>
            </div>

            <div>
              <label htmlFor="setup-password" className="block text-sm font-medium text-slate-700">
                New password (12+ characters)
              </label>
              <input
                id="setup-password"
                name="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="setup-confirm" className="block text-sm font-medium text-slate-700">
                Confirm password
              </label>
              <input
                id="setup-confirm"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
            </div>

            <TurnstileWidget siteKey={turnstileSiteKey()} onToken={setTurnstileToken} />

            {fieldError !== null && <p className="text-sm text-red-600">{fieldError}</p>}
            {status.isError && <ErrorNotice error={status.error} />}
            {setupMutation.isError && <ErrorNotice error={setupMutation.error} />}

            <button
              type="submit"
              disabled={turnstileToken === null || setupMutation.isPending}
              className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {setupMutation.isPending ? 'Setting up…' : 'Set password and sign in'}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}
